<script setup lang="ts">
import BaseButton from '~/components/common/base-button.vue'
import { useApi } from '~/composables/common/use-api'
import { useLogin } from '~/composables/account/use-login'
import { useUserData } from '~/composables/account/use-user-data'
import { NumberUtils } from '~/utils'
import { HttpRequestMethod } from '~/configs/enums'
import { PROMOTION_PLAN_TYPE } from '~/constants'
import { notificationPopup } from '~/utils/modal'
import { modalList } from '~/resources/popup-notification'
import { useModal } from '~/composables/common/use-modal'

type Props = {
  id: string
  title: string
  groupId: string
  streamId: string
  provider: string
  gameUrl: string
  posterSrc: string
  level: { min: number; max: number }
}

const { id, title, groupId, streamId, provider, gameUrl, posterSrc, level } = defineProps<Props>()

const config = useRuntimeConfig()
const { request } = useApi()
const { isLogged, showModalLogin } = useLogin()
const { currentUser } = useUserData()
const { closeAllModal } = useModal()
const userStore = usePromotionUserStore()

const jwtoken = ref<string>()
const triesLeft = ref(3)
const nanoPlayer = ref()

const nanoPlayerConfig = computed(() => ({
  source: {
    defaults: { service: 'bintu' },
    entries: [
      {
        h5live: {
          rtmp: {
            url: 'rtmp://bintu-splay.nanocosmos.de/splay',
            streamname: streamId
          },
          server: {
            websocket: 'wss://bintu-h5live-secure.nanocosmos.de/h5live/authstream',
            hls: 'https://bintu-h5live-secure.nanocosmos.de/h5live/authhttp/playlist.m3u8'
          },
          security: { jwtoken: jwtoken.value }
        }
      }
    ]
  },
  playback: {
    autoplay: true,
    automute: true,
    muted: true,
    faststart: true,
    keepConnection: true
  },
  style: {
    width: '100%',
    height: '100%',
    poster: posterSrc,
    controls: false,
    interactive: false,
    displayMutedAutoplay: false,
    fullScreenControl: false,
    scaling: 'fill',
    view: true
  },
  events: {
    onError: initNanoPlayer,
    onWebSocketError: initNanoPlayer
  }
}))

const levelString = computed(() => {
  const currency = config.public.MAIN_CREDIT_UNIT
  const min = NumberUtils.formatNumberWithCommas(level.min, currency)
  const max = NumberUtils.formatNumberWithCommas(level.max, currency)
  return `Mức cược: ${min} - ${max}`
})

const initNanoPlayer = async () => {
  if (triesLeft.value-- < 0) {
    return
  }
  try {
    const { data } = await request('https://api-csn-s.gameland.today/api/v1/stream/', {
      method: HttpRequestMethod.POST,
      body: { groupId, streamId, site: window.location.hostname }
    })
    jwtoken.value = data.token
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    nanoPlayer.value = new (window as any).NanoPlayer(id)
    nanoPlayer.value.setup(nanoPlayerConfig.value)
  } catch {
    initNanoPlayer()
  }
}

const handleBetClick = async () => {
  if (!isLogged.value) {
    showModalLogin()
    return
  }

  if (userStore.promotionUser?.plan?.package_id) {
    if ([PROMOTION_PLAN_TYPE.WELCOME, PROMOTION_PLAN_TYPE.FIRST_50].includes(userStore.promotionUser.plan.package_id)) {
      closeAllModal()
      notificationPopup(modalList.GAME_PROMOTION_PC)
      return
    }
  }

  await navigateTo(
    `${gameUrl}?token=${currentUser.value.gptoken}&brand=${config.public.BRAND_NAME}&ru=${config.public.SITE_DOMAIN}`,
    { open: { target: '_blank' } }
  )
}

onMounted(async () => {
  initNanoPlayer()
})

onBeforeUnmount(() => {
  nanoPlayer.value?.destroy()
})
</script>

<template>
  <div class="livestream-card ratio" :style="{ backgroundImage: `url(${posterSrc})` }" @click="handleBetClick">
    <div>
      <div :id="id" class="livestream-card__player"></div>
    </div>

    <div>
      <div class="livestream-card__badge">LIVE</div>

      <div class="livestream-card__game">
        <div class="livestream-card__info">
          <!-- /assets/images/icons/provider/sunwin.webp, /assets/images/icons/provider/789.webp -->
          <NuxtImg :src="`/assets/images/icons/provider/${provider}.webp`" width="48" :alt="provider" />
          <div>
            <div class="livestream-card__title">{{ title }}</div>
            <div class="livestream-card__level">{{ levelString }}</div>
          </div>
        </div>

        <div>
          <BaseButton>Cược ngay</BaseButton>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.livestream-card {
  --aspect-ratio: 56.4%;
  overflow: hidden;
  border-radius: 6px;
  background-size: cover;

  &__player {
    z-index: 1;
  }

  &:deep() {
    [id^='middleView-'] {
      display: none;
    }
  }

  &__badge {
    position: absolute;
    top: 8px;
    right: 8px;
    z-index: 1;
    display: flex;
    align-items: center;
    gap: 2px;
    padding: 4px 8px 4px 4px;
    border-radius: 56px;
    font-size: 12px;
    line-height: 16px;
    color: $white;
    background-color: rgba(48, 48, 48, 0.8);

    &::before {
      content: '';
      width: 12px;
      height: 12px;
      border-radius: 50%;
      opacity: 0.4;
      background-color: $neutral-500;
    }

    &::after {
      content: '';
      position: absolute;
      left: 6.5px;
      width: 7px;
      height: 7px;
      border-radius: 50%;
      background-color: $white;
    }
  }

  &__game {
    position: absolute;
    bottom: 0;
    z-index: 1;
    display: flex;
    justify-content: space-between;
    width: 100%;
    padding: 4px 16px 10px;
    background: linear-gradient(180deg, rgba(70, 70, 70, 0) 0%, rgba(30, 30, 30, 0.72) 43.5%, rgba(7, 7, 7, 0.8) 100%);
  }

  &__info {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__title {
    font-size: 18px;
    font-weight: 700;
    line-height: 24px;
    color: $white;
  }

  &__level {
    font-weight: 500;
    line-height: 24px;
    color: $yellow-500;
  }
}
</style>
